import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Shield, KeyRound, Loader2, CheckCircle, AlertTriangle, Download, Copy, RefreshCw } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useNostr } from '@nostrify/react';
import { HeirShell } from '@/components/HeirShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SignerBadge } from '@/components/nip41/KeyChainDiagram';
import {
  NIP41_KIND_WHITELIST,
  NIP41_KIND_TIMESTAMP_PROOF,
  NIP41_SPEC_WARNING,
  generateSuccessorKey,
  validateNpub,
  buildWhitelistTags,
  buildTimestampProofTags,
  LS_DRAFT_SUCCESSOR,
} from '@/lib/nip41';
import { createTimestampProof, buildTimestampEvent } from '@/lib/otsService';
import { cn } from '@/lib/utils';

type ArmStep = 'connect' | 'successor' | 'review' | 'publish' | 'done';

export default function ArmPage() {
  useSeoMeta({
    title: 'Arm — Heir (NIP-41)',
    description: 'Pre-position a successor key for NIP-41 migration.',
  });

  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: publishEvent } = useNostrPublish();

  const [step, setStep] = useState<ArmStep>('connect');
  const [successorNpub, setSuccessorNpub] = useState('');
  const [generatedKey, setGeneratedKey] = useState<{ nsec: string; npub: string; pubkey: string } | null>(null);
  const [showNsec, setShowNsec] = useState(false);
  const [nsecCopied, setNsecCopied] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedEvents, setPublishedEvents] = useState<any[]>([]);
  const [error, setError] = useState('');

  // Load draft successor from local storage
  useState(() => {
    try {
      const draft = localStorage.getItem(LS_DRAFT_SUCCESSOR);
      if (draft) {
        const parsed = JSON.parse(draft);
        if (parsed.npub) setSuccessorNpub(parsed.npub);
      }
    } catch {
      // ignore
    }
  });

  const handleGenerateSuccessor = () => {
    const key = generateSuccessorKey();
    setGeneratedKey(key);
    setSuccessorNpub(key.npub);
    setShowNsec(true);
    // Save draft
    localStorage.setItem(LS_DRAFT_SUCCESSOR, JSON.stringify({ npub: key.npub, pubkey: key.pubkey }));
  };

  const handleUseExisting = () => {
    const validation = validateNpub(successorNpub);
    if (!validation.valid) {
      setError(validation.error || 'Invalid npub');
      return;
    }
    setError('');
    localStorage.setItem(LS_DRAFT_SUCCESSOR, JSON.stringify({ npub: successorNpub, pubkey: validation.pubkey }));
    setStep('review');
  };

  const handlePublish = async () => {
    if (!user) return;
    setIsPublishing(true);
    setError('');

    try {
      const pubkey = generatedKey?.pubkey || validateNpub(successorNpub).pubkey;
      if (!pubkey) throw new Error('Invalid successor pubkey');

      // 1. Build and publish whitelist event (kind 1776)
      const whitelistTags = buildWhitelistTags(pubkey);
      const whitelistEvent = await publishEvent({
        kind: NIP41_KIND_WHITELIST,
        content: '',
        tags: whitelistTags,
      });

      // 2. Create timestamp proof for the whitelist
      const pendingProof = await createTimestampProof(
        whitelistEvent.id,
        whitelistEvent.id,
        NIP41_KIND_WHITELIST
      );

      // 3. Build and publish timestamp proof event (kind 1040)
      const timestampEventTemplate = buildTimestampEvent(pendingProof, user.pubkey);
      const timestampEvent = await publishEvent({
        kind: NIP41_KIND_TIMESTAMP_PROOF,
        content: timestampEventTemplate.content,
        tags: timestampEventTemplate.tags,
      });

      setPublishedEvents([whitelistEvent, timestampEvent]);
      setStep('done');

      // Clear draft
      localStorage.removeItem(LS_DRAFT_SUCCESSOR);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsPublishing(false);
    }
  };

  const copyNsec = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey.nsec);
      setNsecCopied(true);
      setTimeout(() => setNsecCopied(false), 2000);
    }
  };

  const downloadRecoveryCard = () => {
    if (!publishedEvents.length || !generatedKey) return;
    const card = {
      app: 'Heir (NIP-41 Companion)',
      date: new Date().toISOString(),
      activeNpub: user ? nip19.npubEncode(user.pubkey) : undefined,
      successorNpub: generatedKey.npub,
      successorNsec: generatedKey.nsec,
      whitelistEventId: publishedEvents[0]?.id,
      relayHints: ['wss://relay.ditto.pub', 'wss://relay.primal.net'],
      note: 'Store this securely. The nsec is your break-glass key.',
    };
    const blob = new Blob([JSON.stringify(card, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `heir-recovery-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <HeirShell>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Arm</h1>
          <p className="text-slate-400">
            Pre-position a successor key while your current key is still trusted.
          </p>
        </div>

        {/* Stepper */}
        <StepIndicator currentStep={step} />

        {/* Draft Warning */}
        <div className="mb-6 bg-amber-950/20 border border-amber-500/20 rounded-lg p-3 flex items-start space-x-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300">{NIP41_SPEC_WARNING}</p>
        </div>

        {/* Step Content */}
        <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6">
          {step === 'connect' && (
            <div className="space-y-6">
              <div className="text-center">
                <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Connect Active Key</h2>
                <p className="text-slate-400 text-sm">
                  You will sign the whitelist with your current, trusted key.
                </p>
              </div>

              {user ? (
                <div className="space-y-4">
                  <SignerBadge pubkey={user.pubkey} label="Active Key" color="cyan" />
                  <Button
                    onClick={() => setStep('successor')}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <p className="text-sm text-slate-500">
                    Please log in with your NIP-07 extension to continue.
                  </p>
                  <p className="text-xs text-slate-600">
                    The active key will sign the whitelist event.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 'successor' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Choose Successor</h2>
                <p className="text-slate-400 text-sm">
                  Provide the npub of your successor key, or generate a fresh one.
                </p>
              </div>

              {/* Generate new */}
              {!generatedKey ? (
                <Button
                  onClick={handleGenerateSuccessor}
                  variant="outline"
                  className="w-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-950/30 h-auto py-4"
                >
                  <KeyRound className="w-5 h-5 mr-2" />
                  <div className="text-left">
                    <div className="font-medium">Generate New Successor Key</div>
                    <div className="text-xs opacity-70">Creates a fresh nsec/npub pair</div>
                  </div>
                </Button>
              ) : (
                <div className="bg-cyan-950/20 border border-cyan-500/30 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-cyan-400">Generated Successor</h3>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleGenerateSuccessor}
                      className="text-slate-400 hover:text-cyan-400"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Regenerate
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-black/40 rounded-lg p-3">
                      <code className="text-xs font-mono text-cyan-300">{generatedKey.npub}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(generatedKey.npub);
                        }}
                        className="text-slate-500 hover:text-cyan-400"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>

                    {showNsec && (
                      <div className="bg-red-950/20 border border-red-500/30 rounded-lg p-3 space-y-2">
                        <p className="text-xs text-red-400 font-medium flex items-center space-x-2">
                          <AlertTriangle className="w-3 h-3" />
                          <span>Private Key — Back up now, never shown again</span>
                        </p>
                        <div className="flex items-center justify-between bg-black/40 rounded-lg p-3">
                          <code className="text-xs font-mono text-red-300">{generatedKey.nsec}</code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={copyNsec}
                            className="text-slate-500 hover:text-red-400"
                          >
                            {nsecCopied ? <CheckCircle className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => setStep('review')}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white"
                  >
                    Continue with this key
                  </Button>
                </div>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-800" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-slate-950 text-slate-600">or use existing</span>
                </div>
              </div>

              <div className="flex space-x-3">
                <Input
                  value={successorNpub}
                  onChange={(e) => setSuccessorNpub(e.target.value)}
                  placeholder="Paste successor npub..."
                  className="flex-1 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
                />
                <Button
                  onClick={handleUseExisting}
                  disabled={!successorNpub.startsWith('npub1')}
                  variant="outline"
                  className="border-slate-700 text-slate-400 hover:text-white"
                >
                  Use
                </Button>
              </div>

              {error && (
                <p className="text-sm text-red-400">{error}</p>
              )}
            </div>
          )}

          {step === 'review' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Review Whitelist</h2>
                <p className="text-slate-400 text-sm">
                  Review the event you are about to sign and publish.
                </p>
              </div>

              {user && (
                <SignerBadge pubkey={user.pubkey} label="Will be signed by" color="cyan" />
              )}

              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Event Type</span>
                  <span className="text-xs font-mono text-cyan-400">kind:1776 Whitelist</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Successor</span>
                  <span className="text-xs font-mono text-slate-300">{successorNpub}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Content</span>
                  <span className="text-xs text-slate-400">(empty)</span>
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-300 mb-2">What happens next</h4>
                <ol className="space-y-2 text-sm text-slate-400 list-decimal list-inside">
                  <li>The whitelist event is signed with your active key and published</li>
                  <li>An OpenTimestamps proof is created and published (kind:1040)</li>
                  <li>Both events are broadcast to your configured relays</li>
                </ol>
              </div>

              <div className="bg-amber-950/20 border border-amber-500/20 rounded-lg p-4">
                <p className="text-sm text-amber-300">
                  <strong>Public exposure:</strong> The whitelist is public and links your current key 
                  to the successor. Anyone can see that you have armed this successor.
                </p>
              </div>

              <Button
                onClick={handlePublish}
                disabled={isPublishing}
                className="w-full bg-cyan-600 hover:bg-cyan-500 text-white"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Sign & Publish Whitelist
                  </>
                )}
              </Button>

              {error && (
                <p className="text-sm text-red-400 text-center">{error}</p>
              )}
            </div>
          )}

          {step === 'done' && (
            <div className="space-y-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Whitelist Published</h2>
                <p className="text-slate-400 text-sm">
                  Your successor is now pre-positioned. The whitelist and its timestamp proof 
                  have been published to your relays.
                </p>
              </div>

              {publishedEvents.length > 0 && (
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-left space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Whitelist Event</span>
                    <code className="text-xs font-mono text-cyan-400">{publishedEvents[0].id.slice(0, 16)}...</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Timestamp Proof</span>
                    <code className="text-xs font-mono text-cyan-400">{publishedEvents[1]?.id.slice(0, 16)}...</code>
                  </div>
                </div>
              )}

              {generatedKey && (
                <div className="bg-cyan-950/20 border border-cyan-500/30 rounded-lg p-4 text-left">
                  <h4 className="text-sm font-medium text-cyan-400 mb-2">Recovery Card</h4>
                  <p className="text-xs text-slate-400 mb-3">
                    Download this file and store it securely with your successor nsec.
                  </p>
                  <Button
                    onClick={downloadRecoveryCard}
                    variant="outline"
                    className="w-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-950/30"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Recovery Card
                  </Button>
                </div>
              )}

              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-left">
                <h4 className="text-sm font-medium text-slate-300 mb-2">Next Steps</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Store the successor nsec in cold storage (password manager, hardware wallet, paper)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Periodically inspect your npub to confirm the whitelist is still live</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>After migrating, remember to arm a new successor from the new key</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </HeirShell>
  );
}

function StepIndicator({ currentStep }: { currentStep: ArmStep }) {
  const steps: { id: ArmStep; label: string }[] = [
    { id: 'connect', label: 'Connect' },
    { id: 'successor', label: 'Successor' },
    { id: 'review', label: 'Review' },
    { id: 'publish', label: 'Publish' },
    { id: 'done', label: 'Done' },
  ];

  const currentIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, i) => (
        <div key={step.id} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all',
                i <= currentIndex
                  ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400'
                  : 'border-slate-700 bg-slate-900 text-slate-600'
              )}
            >
              {i < currentIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className={cn('text-[10px] mt-1.5 uppercase tracking-wider', i <= currentIndex ? 'text-cyan-400' : 'text-slate-600')}>
              {step.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={cn('w-8 sm:w-12 h-0.5 mx-1 sm:mx-2', i < currentIndex ? 'bg-cyan-500/50' : 'bg-slate-800')} />
          )}
        </div>
      ))}
    </div>
  );
}

// Need this for the button
function ArrowRight(props: any) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}
